// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import { ReactNode, createElement } from 'react';
import parse, { domToReact, HTMLReactParserOptions, DOMNode, Element } from 'html-react-parser';

const ALLOWED_TAGS = new Set([
    'a',
    'b',
    'blockquote',
    'br',
    'code',
    'em',
    'i',
    'li',
    'ol',
    'p',
    'pre',
    'span',
    'strong',
    'u',
    'ul',
]);

const ALLOWED_ATTRS: Record<string, Set<string>> = {
    a: new Set(['href', 'title', 'target', 'rel', 'data-method']),
};

const SAFE_URL_RE = /^(?:https?:|mailto:|tel:|\/|#)/i;

const VOID_TAGS = new Set(['br']);

const sanitizeAttribs = (tag: string, attribs: Record<string, string>): Record<string, string> => {
    const allowed = ALLOWED_ATTRS[tag];
    if (!allowed) {
        return {};
    }

    const result: Record<string, string> = {};
    for (const [key, value] of Object.entries(attribs)) {
        if (!allowed.has(key)) {
            continue;
        }
        if (key === 'href' && !SAFE_URL_RE.test(value.trim())) {
            continue;
        }
        result[key] = value;
    }

    if (tag === 'a' && result.target === '_blank') {
        result.rel = 'noopener noreferrer';
    }

    return result;
};

const isElement = (node: DOMNode): node is Element => node.type === 'tag' || node.type === 'script' || node.type === 'style';

const options: HTMLReactParserOptions = {
    replace: domNode => {
        if (!isElement(domNode)) {
            return undefined;
        }

        const { name, attribs, children } = domNode;

        if (!ALLOWED_TAGS.has(name)) {
            // eslint-disable-next-line react/jsx-no-useless-fragment
            return <></>;
        }

        const props = sanitizeAttribs(name, attribs);

        if (VOID_TAGS.has(name)) {
            return createElement(name, props);
        }

        return createElement(name, props, domToReact(children as DOMNode[], options));
    },
};

export interface SafeHtmlProps {
    html: string | null | undefined;
}

const SafeHtml = ({ html }: SafeHtmlProps): ReactNode => {
    if (!html) {
        return null;
    }

    return parse(html, options);
};

export default SafeHtml;
